
import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Newspaper, 
  DatabaseIcon, 
  Users, 
  FileText, 
  ChartBar,
  ExternalLink,
  UserCheck
} from 'lucide-react';

interface DataSourcesProps {
  companyName: string;
}

const DataSources: React.FC<DataSourcesProps> = ({ companyName }) => {
  const dataSources = [
    {
      name: 'Media Monitoring',
      icon: Newspaper,
      description: 'News and media mentions analysis',
      linkTo: `/companies/${encodeURIComponent(companyName)}/media-monitoring`
    },
    {
      name: 'Financial Reporting',
      icon: FileText,
      description: 'Company financial health indicators',
      linkTo: `/companies/${encodeURIComponent(companyName)}/financial-reporting`
    },
    {
      name: 'Recruiter Review',
      icon: UserCheck,
      description: 'Insights from industry recruiters',
      linkTo: `/companies/${encodeURIComponent(companyName)}/recruiter-reviews`
    },
    {
      name: 'Employee Reviews',
      icon: Users,
      description: 'Insights from current and former employees'
    },
    {
      name: 'Redundancy Statistics',
      icon: ChartBar,
      description: 'Historical and predicted workforce changes'
    },
    {
      name: 'Investment Tracking',
      icon: DatabaseIcon,
      description: 'Funding rounds and investment activity',
      linkTo: `/companies/${encodeURIComponent(companyName)}/financial-reporting?category=funding`
    }
  ];

  return (
    <div>
      <h2 className="text-lg font-medium text-redflags-dark mb-4">Data Sources for Risk Assessment</h2>
      <p className="text-gray-600 text-sm mb-4">
        Our risk assessment for {companyName} is based on multiple data sources to provide a comprehensive view.
      </p>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {dataSources.map((source) => {
          const SourceIcon = source.icon;
          return (
            <div key={source.name} className={`flex items-start space-x-3 p-3 rounded-lg bg-gray-50 ${source.linkTo ? 'hover:bg-gray-100 cursor-pointer' : ''} transition-colors`}>
              {source.linkTo ? (
                <Link to={source.linkTo} className="flex items-start space-x-3 w-full">
                  <div className="bg-redflags-light/30 p-2 rounded-full flex-shrink-0">
                    <SourceIcon className="w-5 h-5 text-redflags-red" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center">
                      <h3 className="text-sm font-medium text-redflags-dark">{source.name}</h3>
                      <ExternalLink className="w-3 h-3 ml-1 text-redflags-gray" />
                    </div>
                    <p className="text-xs text-gray-500">{source.description}</p>
                  </div>
                </Link>
              ) : (
                <>
                  <div className="bg-redflags-light/30 p-2 rounded-full flex-shrink-0">
                    <SourceIcon className="w-5 h-5 text-redflags-red" />
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-redflags-dark">{source.name}</h3>
                    <p className="text-xs text-gray-500">{source.description}</p>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DataSources;
